import React, { useRef, memo, useEffect, useState } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import listAnalytics from './listAnalytics';
import Video from '../../components/Templates/Video/Video';
import Image from '../../components/Templates/Image/Image';
import TemplateParser from '../../components/Templates/TemplateParser';
import { updatePagination } from '../../actions/environment';
import { unique } from '../../utils/array';
import { addEvent, removeEvent, offset, height } from '../../utils/dom';
import { translate } from '../../utils/locale';
import { extractTemplateFromObj } from '../../utils/object';
import { decode, urlify } from '../../utils/string';
import s from './Pagination.scss';

const areEqual = (prevProps, nextProps) => {
	if (prevProps.list.length === nextProps.list.length) return true;
	return false;
};

const areCollectionsEqual = (prevProps, nextProps) => {
	if (prevProps.currentCollection.length === nextProps.currentCollection.length) return true;
	return false;
};

const Collection = memo(({ currentCollection, collection, title, list, cache, collectionSections, setup, variantClasses, imageColumn, ListComponent, currentView, canonical, currentPath, currentHash, closeSearch, layout }) => {
	const itemsShowing = [];
	let collectionCount = 0;
	let skipProducts = 0;
	let subComponent = [];
	let introSections = [];

	const listing = currentCollection.map((item) => {
		let items = false;
		let id;
		if (list) {
			id = item;
			item = cache[item];
			if (!item) items = Object.keys(cache).filter((cacheID) => cacheID.indexOf(id) !== -1);
		}
		const component = [];

		if (collectionSections?.[collection]?.[collectionCount]) {
			const { extractedSection: tempContent, filteredSections: inlineCategoryContent } = extractTemplateFromObj(collectionSections[collection][collectionCount].sections, 'content', false);
			const inlineMedia = Array.isArray(tempContent) ? tempContent[0].content.inlineMedia : tempContent.content.inlineMedia;
			introSections = [...introSections, ...inlineCategoryContent];

			const filmContent = { ...inlineMedia.film, src: inlineMedia.film?.source?.url };
			if (inlineMedia.image?.url) filmContent.poster = inlineMedia.image.url;

			const inlineCategoryContentTemplate = (
				<div className={s.pagination_collection_media}>
					{
						inlineMedia.film.source ? (
							<Video content={filmContent} column={12} />
						) : (
							<Image content={inlineMedia} column={12} />
						)
					}
				</div>
			);
			component.push(inlineCategoryContentTemplate);

			collectionCount += 1;
		}
		if (skipProducts === 0) subComponent = [];

		if (items) {
			items.forEach((variantID) => {
				const variant = cache[variantID];
				itemsShowing.push(variant);
				component.push(<ListComponent variantClasses={[...variantClasses, skipProducts > 0 ? 'productItem__inlineCategoryContent' : null]} imageColumn={imageColumn} key={variantID} id={variantID} content={variant} canonical={canonical} setup={setup} currentPath={currentPath} currentView={currentView} itemIndex={collectionCount + 2} currentHash={currentHash} closeSearch={closeSearch} layout={layout} />);
				collectionCount += 1;
			});
		} else if (item) {
			itemsShowing.push(item);
			component.push(<ListComponent variantClasses={[...variantClasses, skipProducts > 0 ? 'productItem__inlineCategoryContent' : null]} imageColumn={imageColumn} key={list ? id : urlify(translate(item.uri, setup.language).text)} id={id} content={item} canonical={canonical} setup={setup} currentPath={currentPath} currentView={currentView} itemIndex={collectionCount + 2} currentHash={currentHash} closeSearch={closeSearch} layout={layout} />);
			collectionCount += 1;
		}

		// Products included in CMS template.
		if (skipProducts > 0) {
			skipProducts -= 1;
			collectionCount += 1;
			subComponent = [...subComponent, ...component];
			return skipProducts === 0 ? <div key={collectionCount} className={[s.contentProductCombo, s.contentProductCombo__left].join(' ')}>{ subComponent }</div> : null;
		}
		return component;
	});

	// Change to only send products which are visible.
	// Batch send them.
	// Clear queue
	if (itemsShowing.length > 0) {
		listAnalytics(itemsShowing, setup, currentView);
	}

	return (
		<section className={s.pagination_collection}>
			<header className={s.pagination_collection_header}>
				<h2 className={s.pagination_collection_header_title}>{ title }</h2>
				{
					introSections.length > 0 && (
						<div className={s.pagination_collection_header_content}>
							<TemplateParser key="inlineCategoryContent" sections={introSections} setup={setup} inline />
						</div>
					)
				}
			</header>
			{ listing }
		</section>
	);
}, areCollectionsEqual);

const nullFunction = () => {};
const emptyArray = [];

const Pagination = ({
	ListComponent,
	collectionSections = {},
	variantClasses = emptyArray,
	imageColumn,
	collectionOrder = emptyArray,
	setup,
	location,
	list,
	cache,
	currentPath,
	currentView,
	currentHash = '',
	canonical = false,
	filterOpen = false,
	closeSearch = nullFunction,
	layout = 'default',
}) => {
	const paginationContainer = useRef(null);
	const pageBase = unique(list || cache);
	const collectionsPerPage = 2;
	const initialPage = typeof document === 'undefined' ? 100 : 1;
	const [page, updatedPage] = useState(initialPage);

	useEffect(() => {
		let debounced = false;

		addEvent(window, 'scroll.pagination', () => {
			window.requestAnimationFrame(() => {
				if (!debounced && paginationContainer.current) {
					const paginationTop = offset(paginationContainer.current);
					const paginationHeight = height(paginationContainer.current);

					// Loads in when one window away.
					if (window.pageYOffset + (height(window) * 2) > paginationTop.top + paginationHeight) {
						updatedPage((prevState) => prevState + 1);
						debounced = true;
						window.setTimeout(() => {
							debounced = false;
						}, 500);
					}
				}
			});
		});

		return () => {
			removeEvent(window, 'scroll.pagination');
		};
	}, []);

	// Collect products based on collection only use primary products
	const collectionOrderMapped = collectionOrder.map((a) => urlify(decode(a.value)));
	const collections = pageBase.reduce((acc, item) => {
		const tempItem = cache[item];
		if (!tempItem) return acc;
		const tempCollection = urlify(tempItem.collection);
		if (!tempItem.primary && location.type !== 'SEARCH') return acc;
		if (!acc[tempCollection]) acc[tempCollection] = [];
		acc[tempCollection].push(item);
		return acc;
	}, {});

	let paginationClasses = [s.pagination];
	if (filterOpen) paginationClasses.push(s.pagination__filterOpen);
	if (pageBase.length < 5) paginationClasses.push(s.pagination__centered);
	if (variantClasses) paginationClasses = [...paginationClasses, ...(Array.isArray(variantClasses) ? variantClasses.filter((c) => !!c && c.indexOf('pagination__') !== -1).map((c) => s[c]) : [s[variantClasses.toString()]])];

	const collectionListing = Object.keys(collections).sort((a, b) => {
		if (collectionOrderMapped.indexOf(a) === collectionOrderMapped.indexOf(b)) return 0;
		if (collectionOrderMapped.indexOf(a) > collectionOrderMapped.indexOf(b)) return 1;
		return -1;
	});

	return (
		<div className={paginationClasses.join(' ')} ref={paginationContainer}>
			{
				collectionListing.slice(0, collectionsPerPage * page).map((collection) => <Collection key={collection} currentCollection={collections[collection]} collection={collection} title={decode(collectionOrder.filter((c) => urlify(decode(c.value)) === collection)?.[0]?.value)} list={list} cache={cache} collectionSections={collectionSections} setup={setup} variantClasses={variantClasses} imageColumn={imageColumn} ListComponent={ListComponent} currentView={currentView} canonical={canonical} currentPath={currentPath} currentHash={currentHash} closeSearch={closeSearch} layout={layout} />)
			}
		</div>
	);
};

const mapState = ({ environment, location }, { showAll = false, paging = null, currentHash = '', canonical = false, magazine = false, ...props }) => ({
	pagination: environment.pagination,
	setup: environment.setup,
	collectionOrder: environment.collections.order,
	pdpSwatches: environment.pdpSwatches,
	location,
	showAll,
	paging,
	currentHash,
	canonical,
	magazine,
	...props,
});

export default hot(module)(connect(mapState, { updatePagination })(memo(Pagination, areEqual)));
